import { ISimpleHourRange } from '@/interfaces/ISimpleHourRange';

/**
 * The schedule editor's model (plan §5.14). Stored rows are `HH:MM:SS`
 * (prod) or `HH:MM`; the form works on `HH:MM` blocks (a time window and the
 * days it applies to) and writes new or edited rows as `HH:MM:00`. Rows the
 * owner did not touch come back byte-identical, and legacy day-7 rows
 * («Festivos») are kept untouched and never shown.
 */
export interface IScheduleBlock {
  key: string;
  /** 0 = Sunday … 6 = Saturday. */
  days: number[];
  /** `HH:MM`. */
  from: string;
  to: string;
}

/** What the schedule dialog resolves: the rows (null = «Todo el día»). */
export interface IScheduleDialogResult {
  hourRanges: ISimpleHourRange[] | null;
}

export interface IScheduleForm {
  mode: 'always' | 'some';
  blocks: IScheduleBlock[];
}

/** Display order of the day chips: «L M M J V S D». */
export const SCHEDULE_WEEK: readonly number[] = [1, 2, 3, 4, 5, 6, 0];

/** The legacy «Festivos» row. */
const LEGACY_DAY = 7;

/** The latest time a block can end (the «24» of «Noche (20 a 24)»). */
export const END_OF_DAY = '23:59';

let blockSequence = 0;

export function newScheduleBlock(
  days: number[] = [...SCHEDULE_WEEK],
  from = '12:00',
  to = '16:00'
): IScheduleBlock {
  blockSequence += 1;
  return { key: `b${blockSequence}`, days, from, to };
}

function clock(time: string): string {
  return time.slice(0, 5);
}

function sortDays(days: readonly number[]): number[] {
  return SCHEDULE_WEEK.filter((day) => days.includes(day));
}

/** Rows grouped into blocks of the same window, in the order they first appear. */
export function scheduleFormFromRanges(
  hours: readonly ISimpleHourRange[] | null
): IScheduleForm {
  if (!hours?.length) return { mode: 'always', blocks: [] };

  const blocks: IScheduleBlock[] = [];
  for (const range of hours) {
    if (range.day === LEGACY_DAY) continue;
    const from = clock(range.from);
    const to = clock(range.to);
    const block = blocks.find(
      (candidate) => candidate.from === from && candidate.to === to
    );
    if (block) {
      if (!block.days.includes(range.day)) block.days.push(range.day);
    } else blocks.push(newScheduleBlock([range.day], from, to));
  }
  for (const block of blocks) block.days = sortDays(block.days);
  // Only day-7 rows: «Solo en ciertos horarios» with no blocks, so the owner
  // sees why the product never shows.
  return { mode: 'some', blocks };
}

export type TScheduleError = 'noBlocks' | 'noDays' | 'order';

/** Errors by block key (`form` for the whole form); empty when valid. */
export function validateScheduleForm(
  form: IScheduleForm
): Record<string, TScheduleError> {
  if (form.mode === 'always') return {};
  if (!form.blocks.length) return { form: 'noBlocks' };
  const errors: Record<string, TScheduleError> = {};
  for (const block of form.blocks) {
    if (!block.days.length) errors[block.key] = 'noDays';
    else if (!(clock(block.from) < clock(block.to)))
      errors[block.key] = 'order';
  }
  return errors;
}

function sameRange(a: ISimpleHourRange, b: ISimpleHourRange): boolean {
  return a.day === b.day && a.from === b.from && a.to === b.to;
}

function sameRanges(
  a: readonly ISimpleHourRange[],
  b: readonly ISimpleHourRange[]
): boolean {
  if (a.length !== b.length) return false;
  const rest = [...b];
  return a.every((range) => {
    const index = rest.findIndex((candidate) => sameRange(candidate, range));
    if (index === -1) return false;
    rest.splice(index, 1);
    return true;
  });
}

/**
 * The rows to save: `null` for «Todo el día»; otherwise one row per block
 * day, reusing the stored row (byte-identical) when its window is unchanged,
 * then the legacy day-7 rows. The stored array itself comes back when
 * nothing changed.
 */
export function scheduleRangesFromForm(
  form: IScheduleForm,
  stored: readonly ISimpleHourRange[] | null
): ISimpleHourRange[] | null {
  if (form.mode === 'always') return null;

  const original = stored ?? [];
  const rows: ISimpleHourRange[] = [];
  for (const block of form.blocks)
    for (const day of sortDays(block.days)) {
      const from = clock(block.from);
      const to = clock(block.to);
      const kept = original.find(
        (range) =>
          range.day === day &&
          clock(range.from) === from &&
          clock(range.to) === to
      );
      if (
        !rows.some(
          (row) =>
            row.day === day && clock(row.from) === from && clock(row.to) === to
        )
      )
        rows.push(kept ?? { day, from: `${from}:00`, to: `${to}:00` });
    }
  rows.push(...original.filter((range) => range.day === LEGACY_DAY));

  if (stored && sameRanges(rows, stored)) return [...stored];
  return rows;
}

//#region Shortcuts

export type TScheduleDayPreset = 'everyDay' | 'weekdays' | 'weekend';
export type TScheduleTimePreset = 'lunch' | 'night';

export const SCHEDULE_DAY_PRESETS: Record<TScheduleDayPreset, number[]> = {
  everyDay: [...SCHEDULE_WEEK],
  weekdays: [1, 2, 3, 4, 5],
  weekend: [6, 0],
};

export const SCHEDULE_TIME_PRESETS: Record<
  TScheduleTimePreset,
  { from: string; to: string }
> = {
  lunch: { from: '12:00', to: '16:00' },
  night: { from: '20:00', to: END_OF_DAY },
};

//#endregion

//#region Summary

/** A run of consecutive days in display order («L a V») or a single day. */
export interface IDaySpan {
  from: number;
  to: number;
}

/** Days as spans in display order: [1..5] → «L a V»; [1, 3] → «L», «X». */
export function daySpans(days: readonly number[]): IDaySpan[] {
  const ordered = sortDays(days);
  const spans: IDaySpan[] = [];
  let start = -1;
  for (let i = 0; i < ordered.length; i++) {
    const position = SCHEDULE_WEEK.indexOf(ordered[i]);
    const previous = i > 0 ? SCHEDULE_WEEK.indexOf(ordered[i - 1]) : -2;
    if (position !== previous + 1) start = i;
    if (
      i === ordered.length - 1 ||
      SCHEDULE_WEEK.indexOf(ordered[i + 1]) !== position + 1
    )
      spans.push({ from: ordered[start], to: ordered[i] });
  }
  return spans;
}

export interface IScheduleSummaryLine {
  spans: IDaySpan[];
  from: string;
  to: string;
}

/** One line per window, for the editor and card badges; [] = «Todo el día». */
export function scheduleSummary(
  hours: readonly ISimpleHourRange[] | null
): IScheduleSummaryLine[] {
  return scheduleFormFromRanges(hours).blocks.map((block) => ({
    spans: daySpans(block.days),
    from: block.from,
    to: block.to,
  }));
}

//#endregion
